import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { AppStoreBadge, APP_STORE_URL } from './AppStoreBadge';

const Header: React.FC = () => {
  const { user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // /account bounces signed-out visitors to /account/signin itself.
  const links = user
    ? [
        { to: '/faq', label: 'FAQ' },
        { to: '/account', label: 'My account' },
      ]
    : [
        { to: '/faq', label: 'FAQ' },
        { to: '/account', label: 'Manage subscription' },
        { to: '/account/signin', label: 'Login' },
      ];

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'text-[15px] font-medium no-underline transition-colors',
      isActive ? 'text-ink' : 'text-ink-soft hover:text-ink',
    );

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled ? 'backdrop-blur-xl bg-white/85 border-b-hairline' : 'bg-transparent',
      )}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <nav className="flex items-center justify-between h-[68px] md:h-[76px]">
          <Link to="/" className="flex items-center no-underline shrink-0">
            <img src="/images/EASYTOCOOK.png" alt="EasyToCook" className="h-12 md:h-14 object-contain" />
          </Link>

          {/* Centre links (desktop) */}
          <div className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <NavLink key={l.label} to={l.to} className={linkClass}>
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2.5">
            <AppStoreBadge size="sm" className="hidden sm:inline-flex" />
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sm:hidden inline-flex items-center h-10 px-4 rounded-full bg-ink text-white text-[14px] font-semibold no-underline"
            >
              Get the app
            </a>
          </div>
        </nav>
      </div>
    </motion.header>
  );
};

export default Header;
